import { createRootRoute, Outlet, useParams } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useGlobalStream } from "@/lib/live";

/**
 * The app shell: a Base-style inset sidebar (the ask list + fleet/costs nav)
 * beside an inset main surface that every route renders into. The sidebar is
 * the one persistent chrome; the header per route lives inside the inset.
 */
function Shell() {
  // One global SSE subscription for the whole app: it invalidates queries as
  // events land, which is what makes every pane feel live.
  useGlobalStream();

  // Highlight the active ask in the sidebar. Read the conversation id from the
  // matched route params when we're on a conversation page.
  const params = useParams({ strict: false }) as {
    conversationId?: string;
  };

  return (
    <TooltipProvider delayDuration={200}>
      <SidebarProvider
        // h-svh (not just the provider's min-h-svh) CAPS the shell at the
        // viewport: without it the chat column grows past the fold and the
        // composer renders off-screen instead of pinning to the bottom.
        className="h-svh max-h-svh"
        style={{ "--sidebar-width": "18rem" } as CSSProperties}
      >
        <AppSidebar activeId={params.conversationId} />
        <SidebarInset className="min-h-0 overflow-hidden">
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}

export const Route = createRootRoute({ component: Shell });
